import { useEffect, useRef } from 'react'
import { Link } from '@tanstack/react-router'
import { Calendar, History, User } from 'lucide-react'
import { useTranslations } from 'use-intl'

import { PortableTextContent } from '@/components/portable-text'
import { crossedThresholds } from '@/lib/analytics/scroll-depth'
import { trackReadComplete, trackReadDepth } from '@/lib/analytics/events'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { ArticleToc, RelatedPosts } from './article-sidebar'
import { ArticleNav, type NavPost } from './article-nav'
import { ArticleFaq } from './article-faq'
import { AuthorCard, type AuthorCardData } from './author-card'
import { ArticleSources } from './article-sources'
import { RelatedArticles } from './related-articles'
import { SummarizeWith } from './summarize-with'
import { KeyTakeaways } from './key-takeaways'
import { type PostCardData } from './post-card'
import { extractToc } from '../toc'
import { SITE_URL } from '@/lib/seo'
import { type FaqItem } from '@/lib/structured-data'

export interface Post {
  title: string
  excerpt?: string
  body: never[]
  keyTakeaways?: string[]
  publishedAt: string
  _updatedAt: string
  author?: AuthorCardData
  categories?: { title: string; slug: string }[]
  sources?: { label: string; url: string; publisher?: string }[]
  faq?: FaqItem[]
  seoTitle?: string
  seoDescription?: string
  ogImage?: object
  otherSlug?: string
  relatedPosts?: PostCardData[]
  previousPost?: NavPost | null
  nextPost?: NavPost | null
}

interface Props {
  post: Post
  related: PostCardData[]
  locale: string
  slug: string
  // Draft preview for editors: no read analytics.
  preview?: boolean
}

export function ArticlePage({ post, related, locale, slug, preview }: Props) {
  const t = useTranslations('blog')
  const toc = extractToc(post.body)
  const category = post.categories?.[0]
  const articleUrl = `${SITE_URL}/${locale}/blog/${slug}`
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, {
      timeZone: 'Europe/Paris',
      dateStyle: 'long',
    })
  const publishedDate = fmtDate(post.publishedAt)
  const updatedDate = fmtDate(post._updatedAt)

  // Read depth: quarter-thresholds fire `read_depth`, ~90% fires `read_complete`
  // once. Refs (not state) so tracking never triggers a re-render.
  const fired = useRef<Set<number>>(new Set())
  const completed = useRef(false)
  useEffect(() => {
    if (preview) return
    fired.current = new Set()
    completed.current = false
    const onScroll = () => {
      const doc = document.documentElement
      const max = doc.scrollHeight - doc.clientHeight
      if (max <= 0) return
      const percent = Math.round((doc.scrollTop / max) * 100)
      for (const threshold of crossedThresholds(percent, fired.current)) {
        fired.current.add(threshold)
        trackReadDepth({ percent: threshold })
      }
      if (percent >= 90 && !completed.current) {
        completed.current = true
        trackReadComplete()
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [slug, preview])
  // Editor-curated related posts win; fall back to the 6-most-recent list. Cap at 6.
  const relatedArticles = (
    post.relatedPosts?.length ? post.relatedPosts : related
  ).slice(0, 6)

  return (
    <>
      {/* Plain hero band: same abstract-3d background + header-pull as the blog
          index hero, but no wordmark and no borders. Fades into the white page. */}
      <div className="relative isolate -mt-(--header-h) h-[130px] md:h-[280px] overflow-hidden pt-(--header-h)">
        {/* Above-the-fold hero band background: eager + high priority so it
            isn't deferred behind the article body. */}
        <img
          src="/backgrounds/abstract-3d-light.webp"
          alt=""
          aria-hidden
          width={1920}
          height={1080}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="pointer-events-none absolute inset-0 -z-10 size-full scale-[1.3] object-cover object-[35%_85%]"
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-1/2 bg-gradient-to-t from-background to-transparent" />
      </div>

      <div className="mx-auto max-w-6xl px-6 pb-16 md:pb-24 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-14">
          <article className="min-w-0">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/$locale/blog" params={{ locale }}>
                      {t('breadcrumbRoot')}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {category && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>{category.title}</BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>

            <h1 className="mt-4 font-serif text-3xl font-bold text-primary lg:text-4xl">
              {post.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Calendar className="size-4" />
                {publishedDate}
              </span>
              {post.author && (
                <span className="flex items-center gap-1.5">
                  <User className="size-4" />
                  {post.author.name}
                </span>
              )}
              {updatedDate && updatedDate !== publishedDate && (
                <span className="flex items-center gap-1.5">
                  <History className="size-4" />
                  {t('updatedOn', { date: updatedDate })}
                </span>
              )}
            </div>

            <KeyTakeaways
              items={post.keyTakeaways}
              title={t('keyTakeawaysTitle')}
              summary={post.excerpt}
              summaryTitle={t('summaryTitle')}
            />

            {/* On mobile the sommaire sits at the top of the article, before the
              body; on desktop it lives in the sticky sidebar (hidden here). */}
            <ArticleToc
              entries={toc}
              title={t('toc')}
              className="mt-8 lg:hidden"
            />

            <div className="mt-8">
              <PortableTextContent value={post.body} />
            </div>

            <ArticleSources sources={post.sources} title={t('sourcesTitle')} />

            <ArticleFaq items={post.faq} title={t('faqTitle')} />

            <AuthorCard author={post.author} label={t('writtenBy')} />

            <ArticleNav
              previous={post.previousPost ?? null}
              next={post.nextPost ?? null}
              locale={locale}
              label={t('articleNavigation')}
              previousLabel={t('previousArticle')}
              nextLabel={t('nextArticle')}
            />
          </article>

          <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            <ArticleToc
              entries={toc}
              title={t('toc')}
              className="hidden lg:block"
            />
            <hr className="hidden border-border lg:block" />
            <SummarizeWith
              title={t('summarize')}
              articleUrl={articleUrl}
              promptPrefix={t('summarizePrompt')}
            />
            <hr className="border-border" />
            <RelatedPosts
              posts={related.slice(0, 3)}
              locale={locale}
              title={t('related')}
            />
          </aside>
        </div>

        <RelatedArticles
          posts={relatedArticles}
          locale={locale}
          title={t('relatedArticles')}
        />
      </div>
    </>
  )
}
